import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcAssetsV1AssetQueryServiceAssetGet } from '@interxfi/api-client/generated/hooks';
import { useExcIceGsmV1InstrumentQueryServiceInstrumentGet } from '@interxfi/api-client/generated/hooks/exc-ice-gsm-v1-instrument-query-service-instrument-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { DEFAULT_BOX_PADDING } from '@interxfi/design-system/components/box/box';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcIceGsmV1InstrumentState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { getAssetPageDetailListItems } from '../../../../../../../../../../features/asset/asset-page-detail-list-items';
import { IceGsmMarketsTable } from '../../../../../../../../../../features/vendor/ice/global-security-master/ice-gsm-markets-table';
import { IceGsmInstrumentEntityMenu } from '../../../../../../../../../../features/vendor/ice/global-security-master/instrument/ice-gsm-instrument-entity-menu';
import { IceGsmInstrumentEventSummary } from '../../../../../../../../../../features/vendor/ice/global-security-master/instrument/ice-gsm-instrument-event';
import { getIceGsmInstrumentStateListItems } from '../../../../../../../../../../features/vendor/ice/global-security-master/instrument/ice-gsm-instrument-list-items';
import { getIceGsmInstrumentDetailPath } from '../../../../../../../../../../features/vendor/ice/global-security-master/instrument/ice-gsm-instrument-routing';
import { IceGsmInstrumentStatusPill } from '../../../../../../../../../../features/vendor/ice/global-security-master/instrument/ice-gsm-instrument-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/ice/instrument/_instrument/$instrumentId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ice-gsm-instrument-${params.instrumentId}`,
        label: 'Instrument',
        to: getIceGsmInstrumentDetailPath(params.instrumentId),
        suffix: (
          <CopyButton
            content={params.instrumentId}
            label="Copy Instrument ID"
            tooltip={{ copy: params.instrumentId, copied: 'Copied Instrument ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('ICE GSM Instrument'),
  component: IceInstrumentDetail,
});

function getListItems(data: ExcIceGsmV1InstrumentState | undefined) {
  const items = getIceGsmInstrumentStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [items.data.name, items.data.identifiers.cusip, items.data.identifiers.isin, items.data.identifiers.symbol, items.data.ingestedAt],
        },
        {
          id: 'xmlLocation',
          title: 'XML Location',
          items: [
            items.data.xmlLocation.bucket,
            items.data.xmlLocation.key,
            items.data.xmlLocation.offset,
            items.data.xmlLocation.length,
            items.data.xmlHash,
          ],
        },
        ...(items.data.organization
          ? [
              {
                id: 'organization',
                title: 'Organization',
                items: [items.data.organization.name, items.data.organization.abbreviatedName, items.data.organization.country],
              },
            ]
          : []),
        ...(items.data.instrument
          ? [
              {
                id: 'instrument',
                title: 'Instrument',
                items: [
                  items.data.instrument.name,
                  items.data.instrument.assetType,
                  items.data.instrument.country,
                  items.data.instrument.primaryCurrencyCode,
                  items.data.instrument.primaryExchange,
                  items.data.instrument.federalTaxStatus,
                  items.data.instrument.financialTransactionTax,
                  items.data.instrument.status,
                  items.data.instrument.statusReason,
                  items.data.instrument.sector.sector,
                  { ...items.data.instrument.sector.detail.category, title: 'Sector Category' },
                  { ...items.data.instrument.sector.detail.subCategory, title: 'Sector Subcategory' },
                  { ...items.data.instrument.sector.detail.class, title: 'Sector Class' },
                  { ...items.data.instrument.sector.detail.group, title: 'Sector Group' },
                ],
              },
            ]
          : []),
        ...(items.data.trading
          ? [
              {
                id: 'trading',
                title: 'Trading',
                items: [
                  items.data.trading.listingStatus,
                  items.data.trading.listingStatusDetail,
                  items.data.trading.listingDate,
                  items.data.trading.delistingDate,
                ],
              },
            ]
          : []),
        ...(items.data.equity
          ? [
              {
                id: 'equity',
                title: 'Equity',
                items: [
                  items.data.equity.equityType,
                  items.data.equity.preferredType,
                  items.data.equity.warrantType,
                  items.data.equity.entitlementType,
                  items.data.equity.isRegisteredInvestmentCompany,
                  items.data.equity.dtcEligible,
                  items.data.equity.nsccEligible,
                  items.data.equity.marginEligible,
                  items.data.equity.traceEligible,
                ],
              },
            ]
          : []),
        ...(items.data.fund
          ? [
              {
                id: 'fund',
                title: 'Fund',
                items: [
                  items.data.fund.name,
                  items.data.fund.symbol,
                  items.data.fund.type,
                  items.data.fund.fundType,
                  items.data.fund.fundAssetType,
                  items.data.fund.fundStructure,
                  items.data.fund.sponsor,
                  items.data.fund.source,
                  items.data.fund.leverageFactor,
                  items.data.fund.broadOrNarrow,
                  items.data.fund.primarySector,
                  items.data.fund.secondarySector,
                ],
              },
            ]
          : []),
      ] as const satisfies ReadonlyListGroup[],
      right: [
        items.metadata.createdAt,
        items.metadata.updatedAt,
        items.instrumentId,
        ...(items.data.assetId ? [items.data.assetId] : []),
      ] as const satisfies ReadonlyListItem[],
    },
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load ICE GSM instrument', 'view this ICE GSM instrument');

function IceInstrumentDetail() {
  const { instrumentId } = Route.useParams();
  const request = { instrumentId } as const;
  const { data, error, isPending } = useExcIceGsmV1InstrumentQueryServiceInstrumentGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.instrument), [data?.instrument]);
  const {
    data: assetData,
    isPending: assetIsPending,
    error: assetError,
  } = useIxcAssetsV1AssetQueryServiceAssetGet(data?.instrument?.data?.assetId ? { assetId: data.instrument.data.assetId } : undefined);
  const showLinkedAssetPending = Boolean(assetIsPending || assetError);
  const linkedAssetListItems = useMemo(() => getAssetPageDetailListItems(assetData?.asset, undefined), [assetData?.asset]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <IceGsmInstrumentStatusPill isPending={showPending} size="large" status={data?.instrument?.status} />
          </HorizontalStack>

          <IceGsmInstrumentEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <IceGsmInstrumentEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: (
                <VerticalStack gap={4}>
                  <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />

                  {data?.instrument?.data?.assetId ? (
                    <BoxedAppReadonlyList
                      isPending={showLinkedAssetPending}
                      items={linkedAssetListItems.general.left}
                      padding={DEFAULT_BOX_PADDING}
                      title="Linked Asset"
                      titleGap={4}
                    />
                  ) : null}
                </VerticalStack>
              ),
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <IceGsmMarketsTable
          headerActions={<Typography.Level3>Markets</Typography.Level3>}
          markets={data?.instrument?.data?.otherMarkets ?? STABLE_EMPTY_ARRAY_REF}
          primaryMarket={data?.instrument?.data?.market}
          showSkeleton={showPending}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
